import React, { useState, useEffect, memo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Toolbar,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  CalendarToday,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Assignment,
  Phone,
  MoreVert,
  Inventory2,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreHoriz,
} from "@mui/icons-material";
import {
  useDataProvider,
  useNotify,
  SimpleForm,
  SelectInput,
  NumberInput,
  TextInput,
  DateTimeInput,
  SaveButton,
  Button as RAButton,
} from "react-admin";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

// Constantes para los métodos de pago y estados
export const PAYMENT_METHODS = Object.freeze({
  CASH: "Efectivo",
  CARD: "Tarjeta",
  QR: "QR",
  TIGO_MONEY: "Tigo Money",
  COURTESY: "Cortesia",
  GIFT_CARD: "Gift Card",
});

const BOOKING_STATUS_OPTIONS = [
  { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
  { id: "Cotizacion enviada", name: "Cotización enviada" },
  { id: "Pendiente", name: "Pendiente" },
  { id: "Confirmado", name: "Confirmado" },
  { id: "En proceso", name: "En proceso" },
  { id: "Finalizado", name: "Finalizado" },
  { id: "Cancelada por el proveedor", name: "Cancelada por el proveedor" },
  { id: "Cancelada por el cliente", name: "Cancelada por el cliente" },
  { id: "Caducado", name: "Caducado" },
];

const PAYMENT_STATUS_OPTIONS = [
  { id: "Pendiente", name: "Pendiente" },
  { id: "Pagado", name: "Pagado" },
];

// Componente para mostrar información con íconos (igual que en ServicePage)
const InfoItem = ({ icon, label, value }) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
      <Box display="flex" alignItems="center">
        {icon}
        <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
          {label}:
        </Typography>
      </Box>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

// Componente para WhatsApp (igual que en ServicePage)
const WhatsAppField = ({ phone, countryCode = "+591" }) => {
  if (!phone) return <Typography variant="body2">No disponible</Typography>;
  return (
    <a
      href={`https://wa.me/${countryCode}${phone}`}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "underline", color: "#FF770D" }}
    >
      {phone}
    </a>
  );
};

// Componente para el menú de acciones de cada fila
const BookingActionsMenu = ({ booking, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    onEdit(booking);
    handleClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    onDelete(booking);
    handleClose();
  };

  return (
    <>
      <IconButton size="small" onClick={handleClick} sx={{ color: "text.secondary" }}>
        <MoreHoriz />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 16 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
          Eliminar
        </MenuItem>
      </Menu>
    </>
  );
};

// Función para obtener los estados disponibles según el estado actual
const status = (record) => {
  if (record.status === "Pendiente de cotizacion") {
    return [
      { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
      { id: "Confirmado", name: "Enviar cotización y confirmar" },
      { id: "Cancelada por el proveedor", name: "Cancelado" },
    ];
  }
  if (record.status === "Cotizacion enviada") {
    return [{ id: "Cotizacion enviada", name: "Cotización enviada" }];
  }
  if (record.status === "Pendiente") {
    return [
      { id: "Pendiente", name: "Pendiente" },
      { id: "Confirmado", name: "Confirmar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Confirmado") {
    return [
      { id: "Confirmado", name: "Confirmado" },
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "En proceso") {
    return [
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Finalizado") {
    return [{ id: "Finalizado", name: "Finalizado" }];
  }
  if (record.status === "Cancelada por el proveedor") {
    return [{ id: "Cancelada por el proveedor", name: "Cancelado por el proveedor" }];
  }
  if (record.status === "Cancelada por el cliente") {
    return [{ id: "Cancelada por el cliente", name: "Cancelado por el cliente" }];
  }
};

// Componente para cambiar el estado de la reserva
const BookingStatus = memo(({ record, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState(status(record));
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const handleChange = async (newStatus) => {
    setLoading(true);
    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { status: newStatus, previousStatus: record.status },
        meta: { customAction: "updateStatus" },
      });

      setChoices(status({ ...record, status: newStatus }));

      // Llamar a la función de callback para actualizar los datos del padre
      if (onStatusChange) {
        onStatusChange(record._id, newStatus);
      }

      notify("Estado actualizado correctamente", { type: "success" });
    } catch (error) {
      notify("Error al actualizar el estado", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <SimpleForm toolbar={false}>
        <SelectInput
          source="status"
          label="Estado de Reserva"
          choices={choices || []}
          disabled={loading}
          defaultValue={record.status}
          onChange={(e) => handleChange(e.target.value)}
          style={{
            margin: 0,
            padding: 0,
            minWidth: "150px",
          }}
        />
      </SimpleForm>
    </div>
  );
});

// Componente para editar una sesión específica del paquete
const PackageSessionEditDialog = ({ open, handleClose, booking, onSuccess }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const formatDate = (dateString) => {
    return moment(dateString).format("YYYY-MM-DDTHH:mm");
  };

  const handleEditSave = async (values) => {
    try {
      await dataProvider.update("booking", {
        id: booking._id,
        data: {
          notes: values.notes,
          status: values.status,
          totalEstimatedWorkMinutes: values.totalEstimatedWorkMinutes,
          currentSession: values.currentSession,
          paymentInfo: {
            ...booking.paymentInfo,
            totalPrice: values.totalPrice,
            paymentMethod: values.paymentMethod,
            status: values.paymentStatus,
          },
        },
        previousData: booking,
      });

      notify("Sesión actualizada exitosamente", { type: "success" });
      onSuccess();
      handleClose();
    } catch (error) {
      notify(`Error al actualizar sesión: ${error.message}`, { type: "error" });
    }
  };

  if (!booking) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            Editar Sesión #{booking.currentSession || "N/A"} - {booking.customer?.fullName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <SimpleForm
            record={{
              ...booking,
              bookingDate: formatDate(booking.bookingDate),
              totalPrice: booking.paymentInfo?.totalPrice,
              paymentMethod: booking.paymentInfo?.paymentMethod,
              paymentStatus: booking.paymentInfo?.status,
            }}
            onSubmit={handleEditSave}
            toolbar={
              <Toolbar sx={{ display: "flex", justifyContent: "flex-start", px: 0 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <SaveButton variant="contained" sx={{ py: 1.5, color: "white" }} label="Guardar Cambios" />
                  <RAButton onClick={handleClose} color="inherit">
                    Cancelar
                  </RAButton>
                </Box>
              </Toolbar>
            }
          >
            {/* Información de la Sesión */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
              Información de la Sesión
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <NumberInput source="currentSession" label="Número de Sesión" sx={{ flex: 1 }} min={1} />
              <NumberInput
                source="totalEstimatedWorkMinutes"
                label="Duración estimada (minutos)"
                sx={{ flex: 1 }}
                min={1}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <DateTimeInput source="bookingDate" label="Fecha y hora de la reserva" sx={{ flex: 1 }} disabled />
              <SelectInput
                source="status"
                label="Estado de la reserva"
                choices={BOOKING_STATUS_OPTIONS}
                sx={{ flex: 1 }}
              />
            </Box>

            <TextInput source="notes" label="Notas" multiline rows={3} fullWidth sx={{ mb: 2 }} />

            {/* Información de Pago */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "primary.main" }}>
              Información de Pago
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <NumberInput source="totalPrice" label="Precio total (Bs.)" sx={{ flex: 1 }} min={0} />
              <SelectInput
                source="paymentStatus"
                label="Estado de pago"
                choices={PAYMENT_STATUS_OPTIONS}
                sx={{ flex: 1 }}
              />
            </Box>

            <SelectInput
              source="paymentMethod"
              label="Método de pago"
              choices={Object.values(PAYMENT_METHODS).map((method) => ({
                id: method,
                name: method,
              }))}
              sx={{ mb: 2 }}
            />

            {/* Información del Servicio (Solo lectura) */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "primary.main" }}>
              Servicio (Solo lectura)
            </Typography>

            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mb: 2 }}>
              <Typography>
                <strong>Servicio:</strong> {booking.serviceCart?.[0]?.service?.name || "N/A"}
              </Typography>
              <Typography>
                <strong>Precio unitario:</strong> Bs. {booking.serviceCart?.[0]?.service?.unitPrice || 0}
              </Typography>
              <Typography>
                <strong>Cantidad:</strong> {booking.serviceCart?.[0]?.quantity || 1}
              </Typography>
            </Box>

            {/* Información del Cliente */}
            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>
              Información del Cliente
            </Typography>

            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mb: 2 }}>
              <Typography>
                <strong>Nombre:</strong> {booking.customer?.fullName}
              </Typography>
              <Typography>
                <strong>Teléfono:</strong> {booking.customer?.phone}
              </Typography>
              {booking.customer?.email && (
                <Typography>
                  <strong>Email:</strong> {booking.customer?.email}
                </Typography>
              )}
            </Box>

            {/* Información del Empleado */}
            {booking.employee && (
              <>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
                  Empleado Asignado
                </Typography>
                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography>
                    <strong>Nombre:</strong> {booking.employee?.fullName}
                  </Typography>
                  <Typography>
                    <strong>Teléfono:</strong> {booking.employee?.phone}
                  </Typography>
                </Box>
              </>
            )}
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PackageDetailsPage = () => {
  const { id } = useParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  // Estados para el diálogo de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Estados para el diálogo de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Obtener el providerId del localStorage
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

  // Función para manejar el cambio de estado de una reserva
  const handleStatusChange = (bookingId, newStatus) => {
    setPackageData((prevData) => {
      if (!prevData || !prevData.bookings) return prevData;

      const updatedBookings = prevData.bookings.map((booking) =>
        booking._id === bookingId ? { ...booking, status: newStatus } : booking
      );

      return { ...prevData, bookings: updatedBookings };
    });
  };

  // Función para manejar la edición de una sesión
  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditDialogOpen(true);
  };

  // Función para manejar la eliminación de una sesión
  const handleDeleteBooking = (booking) => {
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  // Función para cerrar el diálogo de edición
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedBooking(null);
  };

  // Función para cerrar el diálogo de confirmación de eliminación
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  // Función para confirmar y ejecutar la eliminación
  const handleConfirmDelete = async () => {
    if (!bookingToDelete) return;

    try {
      await dataProvider.delete("booking", {
        id: bookingToDelete._id,
        previousData: bookingToDelete,
      });

      notify("Sesión eliminada exitosamente", { type: "success" });
      handleCloseDeleteDialog();
      handleBookingSuccess();
    } catch (error) {
      notify(`Error al eliminar sesión: ${error.message}`, { type: "error" });
    }
  };

  // Función para refrescar los datos después de editar/eliminar
  const handleBookingSuccess = async () => {
    try {
      const response = await dataProvider.getOne("package", {
        id,
        meta: { providerId },
      });
      setPackageData(response.data);
    } catch (error) {
      notify("Error al recargar los datos del paquete", { type: "error" });
    }
  };

  useEffect(() => {
    const fetchPackageDetails = async () => {
      try {
        setLoading(true);
        const response = await dataProvider.getOne("package", {
          id,
          meta: { providerId },
        });
        setPackageData(response.data);
      } catch (error) {
        notify("Error al cargar los detalles del paquete", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (id && providerId) {
      fetchPackageDetails();
    }
  }, [id, providerId, dataProvider, notify]);

  const handleGoBack = () => {
    window.history.back();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "activo":
        return "#2e7d32";
      case "expirado":
        return "#d32f2f";
      case "completado":
        return "#424242";
      case "no usado":
        return "#6d4c41";
      default:
        return "#424242";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "activo":
        return <HourglassEmpty sx={{ color: "#2e7d32" }} />;
      case "expirado":
        return <Cancel sx={{ color: "#d32f2f" }} />;
      case "completado":
        return <CheckCircle sx={{ color: "#424242" }} />;
      case "no usado":
        return <Assignment sx={{ color: "#6d4c41" }} />;
      default:
        return <Assignment sx={{ color: "#424242" }} />;
    }
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {/* Skeleton Panel izquierdo - Datos del Paquete (1/3) */}
        <Grid item xs={12} md={3} sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              {/* Skeleton Encabezado */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box flexGrow={1} sx={{ ml: 2 }}>
                  <Skeleton variant="text" width="80%" height={28} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
                <Skeleton variant="circular" width={24} height={24} />
              </Box>

              <Skeleton variant="rectangular" width="100%" height={1} sx={{ my: 2 }} />

              {/* Skeleton Información del Cliente */}
              <Box mt={2}>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Box display="flex" alignItems="center">
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="text" width={80} height={20} sx={{ ml: 1 }} />
                  </Box>
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Box display="flex" alignItems="center">
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="text" width={70} height={20} sx={{ ml: 1 }} />
                  </Box>
                  <Skeleton variant="text" width={90} height={20} />
                </Box>
              </Box>

              <Skeleton variant="rectangular" width="100%" height={1} sx={{ my: 2 }} />

              {/* Skeleton Información del Paquete */}
              <Box mt={2}>
                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                {[1, 2, 3, 4].map((item) => (
                  <Box key={item} display="flex" justifyContent="space-between" mt={1}>
                    <Box display="flex" alignItems="center">
                      <Skeleton variant="circular" width={16} height={16} />
                      <Skeleton variant="text" width={100} height={20} sx={{ ml: 1 }} />
                    </Box>
                    <Skeleton variant="text" width={60} height={20} />
                  </Box>
                ))}

                {/* Skeleton Progreso */}
                <Box mt={2}>
                  <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width="30%" height={16} sx={{ mt: 0.5 }} />
                </Box>

                {/* Skeleton Estado */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Box display="flex" alignItems="center">
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton variant="text" width={60} height={20} sx={{ ml: 1 }} />
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 3 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Skeleton Panel derecho - Tablas (2/3) */}
        <Grid item xs={12} md={9} sx={{ mt: 3 }}>
          {/* Skeleton Primera tabla */}
          <Card
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                        <TableCell key={col} align="center">
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3].map((row) => (
                      <TableRow key={row}>
                        {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                          <TableCell key={col} align="center">
                            <Skeleton variant="text" width="100%" height={20} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Skeleton Segunda tabla */}
          <Card
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              mt: 3,
            }}
          >
            <CardContent>
              <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[1, 2, 3, 4].map((col) => (
                        <TableCell key={col} align="center">
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2].map((row) => (
                      <TableRow key={row}>
                        {[1, 2, 3, 4].map((col) => (
                          <TableCell key={col} align="center">
                            <Skeleton variant="text" width="100%" height={20} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {/* Skeleton fila del total */}
                    <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableCell colSpan={2} align="center">
                        <Skeleton variant="text" width="60%" height={24} />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton variant="text" width="80%" height={32} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton variant="text" width="20%" height={20} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  if (!packageData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>No se encontraron datos del paquete</Typography>
      </Box>
    );
  }

  const progressPercentage = ((packageData.used || 0) / (packageData.totalOfSessions || 1)) * 100;

  // Función para extraer todos los pagos parciales de todos los bookings
  const getAllPartialPayments = () => {
    if (!packageData.bookings) return [];

    const allPayments = [];
    packageData.bookings.forEach((booking) => {
      if (booking.paymentInfo && booking.paymentInfo.partialPagos) {
        booking.paymentInfo.partialPagos.forEach((pago) => {
          allPayments.push({
            ...pago,
            bookingId: booking._id,
            bookingDate: booking.bookingDate,
          });
        });
      }
    });

    // Ordenar por fecha (más reciente primero)
    return allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Calcular el total de todos los pagos parciales
  const getTotalPartialPayments = () => {
    const allPayments = getAllPartialPayments();
    return allPayments.reduce((total, pago) => total + (pago.monto || 0), 0);
  };

  const allPartialPayments = getAllPartialPayments();
  const totalPartialPayments = getTotalPartialPayments();

  // El precio total del paquete (unitPrice ya es el total completo)
  const totalPackagePrice = packageData.unitPrice || 0;
  const paymentPercentage = totalPackagePrice > 0 ? Math.round((totalPartialPayments / totalPackagePrice) * 100) : 0;

  return (
    <Grid container spacing={2}>
      {/* Panel izquierdo - Datos del Paquete (1/3) */}
      <Grid item xs={12} md={3} sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            {/* Encabezado con botón volver y menú */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box flexGrow={1}>
                <Typography variant="h6" fontWeight="bold">
                  {packageData.serviceName || "Paquete"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detalles del paquete
                </Typography>
              </Box>
              <IconButton onClick={handleMenuOpen}>
                <MoreVert />
              </IconButton>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleMenuClose}>Ver más detalles</MenuItem>
            </Menu>

            <Divider sx={{ my: 2, bgcolor: "rgba(0, 0, 0, 0.1)" }} />

            {/* Información del Cliente */}
            <Box mt={2}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Información del Cliente
              </Typography>

              <InfoItem
                icon={<Person fontSize="small" />}
                label="Cliente"
                value={packageData.clientName || "No disponible"}
              />

              <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                <Box display="flex" alignItems="center">
                  <Phone fontSize="small" />
                  <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
                    Teléfono:
                  </Typography>
                </Box>
                <WhatsAppField phone={packageData.clientPhone} />
              </Box>
            </Box>

            <Divider sx={{ my: 2, bgcolor: "rgba(0, 0, 0, 0.1)" }} />

            {/* Información del Paquete */}
            <Box mt={2}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Información del Paquete
              </Typography>

              <InfoItem
                icon={<Inventory2 fontSize="small" />}
                label="Servicio"
                value={packageData.packageName || "No disponible"}
              />

              <InfoItem
                icon={<Assignment fontSize="small" />}
                label="Sesiones Totales"
                value={packageData.totalOfSessions || 0}
              />

              <InfoItem icon={<CheckCircle fontSize="small" />} label="Sesiones Usadas" value={packageData.used || 0} />

              <InfoItem
                icon={<HourglassEmpty fontSize="small" />}
                label="Sesiones Restantes"
                value={(packageData.totalOfSessions || 0) - (packageData.used || 0)}
              />

              {/* Progreso del paquete */}
              <Box mt={2}>
                <Typography variant="body2" fontWeight="bold" mb={1}>
                  Progreso del Paquete:
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: progressPercentage === 100 ? "#4caf50" : "#FF770D",
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progressPercentage)}% completado
                </Typography>
              </Box>

              {/* Estado y Expiración */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                <Box display="flex" alignItems="center">
                  {getStatusIcon(packageData.status)}
                  <Typography variant="body2" fontWeight="bold" sx={{ ml: 1 }}>
                    Estado:
                  </Typography>
                </Box>
                <Chip
                  label={packageData.status || "No usado"}
                  sx={{
                    backgroundColor: getStatusColor(packageData.status),
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Box>

              {packageData.expirationDate && (
                <InfoItem
                  icon={<CalendarToday fontSize="small" />}
                  label="Expira"
                  value={moment(packageData.expirationDate).format("DD/MM/YYYY")}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Panel derecho - Lista de Bookings (2/3) */}
      <Grid item xs={12} md={9} sx={{ mt: 3 }}>
        <Card
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Historial de servicios del paquete
            </Typography>

            {packageData.bookings && packageData.bookings.length > 0 ? (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="tabla de reservas">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">Empleado</TableCell>
                      <TableCell align="center">Fecha de Reserva</TableCell>
                      {/* <TableCell align="center">Servicios</TableCell> */}
                      <TableCell align="center">Sucursal</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Precio (Bs.)</TableCell>
                      <TableCell align="center">Numero de sesion</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packageData.bookings.map((booking) => (
                      <TableRow
                        key={booking._id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          window.location.href = `#/booking/${booking._id}`;
                        }}
                      >
                        <TableCell align="center">
                          {booking._id ? String(booking._id).substring(0, 8) : "N/A"}
                        </TableCell>
                        <TableCell align="center">{booking?.employee?.fullName || "No asignado"}</TableCell>
                        <TableCell align="center">
                          {booking?.bookingDate ? moment(booking.bookingDate).format("DD/MM/YYYY HH:mm") : "Sin fecha"}
                        </TableCell>
                        {/* <TableCell align="center">
                          {booking?.serviceCart && booking.serviceCart.length > 0
                            ? booking.serviceCart.map((item) => item?.service?.name || "Servicio").join(", ")
                            : "Sin servicios"}
                        </TableCell> */}
                        <TableCell align="center">{booking?.branch?.name || "Sin sucursal"}</TableCell>
                        <TableCell align="center">
                          <BookingStatus record={booking} onStatusChange={handleStatusChange} />
                        </TableCell>
                        <TableCell align="center">{booking?.paymentInfo?.totalPrice || "0"}</TableCell>
                        <TableCell align="center">{booking?.currentSession || 0}</TableCell>
                        <TableCell align="center">
                          <BookingActionsMenu
                            booking={booking}
                            onEdit={handleEditBooking}
                            onDelete={handleDeleteBooking}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography variant="body1" color="text.secondary">
                  No hay reservas para este paquete
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Segunda tabla - Historial de Pagos Parciales */}
        <Card
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            mt: 3,
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Historial de pagos parciales
            </Typography>

            {allPartialPayments && allPartialPayments.length > 0 ? (
              <>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="tabla de pagos parciales">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">ID de Pago</TableCell>
                        <TableCell align="center">Fecha de Pago</TableCell>
                        <TableCell align="center">Monto (Bs.)</TableCell>
                        <TableCell align="center">Booking ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allPartialPayments.map((pago) => (
                        <TableRow
                          key={`${pago.bookingId}-${pago._id}`}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          <TableCell align="center">{pago._id ? String(pago._id).substring(0, 8) : "N/A"}</TableCell>
                          <TableCell align="center">
                            {pago.date ? moment(pago.date).format("DD/MM/YYYY HH:mm") : "Sin fecha"}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {pago.monto || 0} Bs.
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {pago.bookingId ? String(pago.bookingId).substring(0, 8) : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Fila del total */}
                      <TableRow sx={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                        <TableCell align="center" colSpan={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            PROGRESO DE PAGOS:
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {totalPartialPayments} / {totalPackagePrice} Bs.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {paymentPercentage}% pagado
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography variant="body1" color="text.secondary">
                  No hay pagos parciales registrados
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Diálogo de edición de sesión */}
      <PackageSessionEditDialog
        open={editDialogOpen}
        handleClose={handleCloseEditDialog}
        booking={selectedBooking}
        onSuccess={handleBookingSuccess}
      />

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta sesión? Esta acción no se puede deshacer.</Typography>
          {bookingToDelete && (
            <Typography sx={{ mt: 1, fontWeight: "bold" }}>
              Sesión #{bookingToDelete.currentSession || "N/A"} - {bookingToDelete.customer?.fullName}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar Sesión
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PackageDetailsPage;
